import React, { useState } from 'react';
import { User, MapPin, Package, FileText, Edit2, Save, X } from 'lucide-react';
import { format } from 'date-fns';

export default function DataExtractionPanel({ reservations, selectedReservation, onSelectReservation, onUpdateReservation }) {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);

  const handleEdit = (reservation) => {
    setEditMode(true);
    setEditData({
      customerName: reservation?.customer_name,
      customerPhone: reservation?.customer_phone,
      customerEmail: reservation?.customer_email || '',
      pickupAddress: reservation?.pickup_address,
      pickupCity: reservation?.pickup_city || '',
      pickupPostalCode: reservation?.pickup_postal_code || '',
      deliveryAddress: reservation?.delivery_address,
      deliveryCity: reservation?.delivery_city || '',
      deliveryCountry: reservation?.delivery_country || 'Côte d\'Ivoire',
      collectionDate: reservation?.collection_date || '',
      serviceType: reservation?.service_type || '',
      itemDescription: reservation?.item_description || '',
      specialInstructions: reservation?.special_instructions || ''
    });
  };

  const handleSave = () => {
    if (selectedReservation && editData) {
      onUpdateReservation(selectedReservation?.id, editData);
      setEditMode(false);
      setEditData(null);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData(null);
  };

  return (
    <div className="space-y-6">
      {/* Reservation List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Réservations Récentes</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {reservations?.slice(0, 20)?.map((reservation) => (
              <button
                key={reservation?.id}
                onClick={() => {
                  onSelectReservation(reservation);
                  setEditMode(false);
                  setEditData(null);
                }}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  selectedReservation?.id === reservation?.id
                    ? 'border-green-500 bg-green-50' :'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{reservation?.customer_name}</div>
                <div className="text-sm text-gray-600">{reservation?.customer_phone}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {format(new Date(reservation?.created_at), 'dd/MM/yyyy HH:mm')}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Extraction Details */}
        <div className="lg:col-span-2">
          {selectedReservation ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Données Extraites</h3>
                {!editMode ? (
                  <button
                    onClick={() => handleEdit(selectedReservation)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit2 size={18} />
                    <span>Modifier</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Save size={18} />
                      <span>Enregistrer</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Customer Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <User size={16} />
                    <span>Informations Client</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom Complet</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.customerName}
                          onChange={(e) => setEditData({ ...editData, customerName: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.customer_name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      {editMode ? (
                        <input
                          type="tel"
                          value={editData?.customerPhone}
                          onChange={(e) => setEditData({ ...editData, customerPhone: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.customer_phone}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      {editMode ? (
                        <input
                          type="email"
                          value={editData?.customerEmail}
                          onChange={(e) => setEditData({ ...editData, customerEmail: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.customer_email || 'Non fourni'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pickup Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <MapPin size={16} />
                    <span>Adresse de Collecte</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.pickupAddress}
                          onChange={(e) => setEditData({ ...editData, pickupAddress: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.pickup_address}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.pickupCity}
                          onChange={(e) => setEditData({ ...editData, pickupCity: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.pickup_city || 'Non spécifié'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Code Postal</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.pickupPostalCode}
                          onChange={(e) => setEditData({ ...editData, pickupPostalCode: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.pickup_postal_code || 'Non spécifié'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delivery Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <MapPin size={16} />
                    <span>Adresse de Livraison</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.deliveryAddress}
                          onChange={(e) => setEditData({ ...editData, deliveryAddress: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.delivery_address}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.deliveryCity}
                          onChange={(e) => setEditData({ ...editData, deliveryCity: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.delivery_city || 'Non spécifié'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.deliveryCountry}
                          onChange={(e) => setEditData({ ...editData, deliveryCountry: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.delivery_country}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <Package size={16} />
                    <span>Détails du Service</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de Collecte</label>
                      {editMode ? (
                        <input
                          type="date"
                          value={editData?.collectionDate}
                          onChange={(e) => setEditData({ ...editData, collectionDate: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {selectedReservation?.collection_date 
                            ? format(new Date(selectedReservation?.collection_date), 'dd/MM/yyyy')
                            : 'Non spécifiée'
                          }
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type de Service</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData?.serviceType}
                          onChange={(e) => setEditData({ ...editData, serviceType: e?.target?.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.service_type || 'Non spécifié'}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      {editMode ? (
                        <textarea
                          value={editData?.itemDescription}
                          onChange={(e) => setEditData({ ...editData, itemDescription: e?.target?.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.item_description || 'Non fournie'}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Instructions Spéciales</label>
                      {editMode ? (
                        <textarea
                          value={editData?.specialInstructions}
                          onChange={(e) => setEditData({ ...editData, specialInstructions: e?.target?.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedReservation?.special_instructions || 'Aucune'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">Sélectionnez une réservation pour voir les détails extraits</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}